import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { BehaviorSubject } from 'rxjs';
import { createSelectOptionsFromEnum, createSelectOptionsFromStringArray } from 'src/app/common/selectOptionsUtils';
import { configurationFieldState } from 'src/app/components/playbooks/playbooks-trigger/triggers/common/enums/configuration-field-state';
import {
  BusinessCriticality,
  Platform,
  ResourceType,
  ResourceTypesPerPlatform,
  SecurityRisk,
} from 'src/app/models/api/common';
import { PolicyModel } from 'src/app/models/api/governance/policy.model';
import { ResourceStatus } from 'src/app/models/api/inventoryResource.model';
import { NewResourceFoundTriggerConfigModel } from 'src/app/models/api/playbooks/triggers/newResourceTriggerModel';
import { PlaybooksTriggerConfigInput } from 'src/app/models/componentInputs/playbooksTriggerInput.interface';
import { ISelectOption } from 'src/app/models/selectOptions';
import { BeautifyDisplayStringPipe } from 'src/app/pipes/beautify-display-string/beautify-display-string.pipe';
import { LabelsProviderPipe } from 'src/app/pipes/labels-provider/labels-provider.pipe';
import { PoliciesHttpService } from 'src/app/services/httpServices/governance/policiesHttpService/policies-http.service';
import { HttpErrorHandlerService } from 'src/app/services/httpServices/httpErrorHandlerService/http-error-handler.service';
import { StoreService } from 'src/app/services/infra/store/store.service';
import { newResourceConfigurationField } from './enums/newResourceConfigurationFields';
import { AppValidators } from 'src/app/common/appValidators';
import { GenerativeAiOptionsSelectOptions } from 'src/app/models/selectOptions/generativeAiOptionsSelectOptions.model';
import { ResourceTypeDisplayNamePipe } from '../../../../../pipes/resource-type-display-name-pipe/resource-type-display-name-pipe';
import { PlaybookTemplatesService } from 'src/app/services/templates/playbook/playbook-templates.service';
import { unsupportedResourceTypesByPlatform, unsupportedAllResourceTypes } from '../common/consts';

@Component({
  selector: 'app-new-resource-trigger',
  templateUrl: './new-resource-trigger.component.html',
  styleUrls: ['./new-resource-trigger.component.scss'],
})
export class NewResourceTriggerComponent implements OnInit {
  @Input() public fg: UntypedFormGroup;
  @Input() public playbooksTriggerInput: PlaybooksTriggerConfigInput;
  @Output() public validConfiguration = new EventEmitter<boolean>();

  public resourceTypeOptions$: BehaviorSubject<ISelectOption[]> = new BehaviorSubject<ISelectOption[]>([]);
  public categorizedOptions$: BehaviorSubject<ISelectOption[]> = new BehaviorSubject<ISelectOption[]>([]);
  public resourceStatusOptions$: BehaviorSubject<ISelectOption[]> = new BehaviorSubject<ISelectOption[]>(
    createSelectOptionsFromEnum(ResourceStatus, this._beautifyPipe)
  );
  public resourceSecurityRiskOptions$: BehaviorSubject<ISelectOption[]> = new BehaviorSubject<ISelectOption[]>(
    createSelectOptionsFromEnum(SecurityRisk, this._beautifyPipe)
  );
  public resourceBusinessCriticalityOptions$: BehaviorSubject<ISelectOption[]> = new BehaviorSubject<ISelectOption[]>(
    createSelectOptionsFromEnum(BusinessCriticality, this._beautifyPipe)
  );
  public resourceGenerativeAiOptions$: BehaviorSubject<ISelectOption[]> = new BehaviorSubject<ISelectOption[]>(
    this._createGenerativeAiOptionsSelectOptions()
  );
  public connectorsOptions$: BehaviorSubject<ISelectOption[]> = new BehaviorSubject<ISelectOption[]>([]);
  public configurationFieldsOptions$: BehaviorSubject<ISelectOption[]> = new BehaviorSubject<ISelectOption[]>([]);

  public multipleResourceTypesWarningVisible = false;
  public createdSinceDatesBehavioralSubject$: BehaviorSubject<Date | undefined>;
  public createdUntilDatesBehavioralSubject$: BehaviorSubject<Date | undefined>;
  public firstSeenSinceDatesBehavioralSubject$: BehaviorSubject<Date | undefined>;
  public firstSeenUntilDatesBehavioralSubject$: BehaviorSubject<Date | undefined>;

  public allConfigurationFieldsState: {
    [id: string]: configurationFieldState;
  };
  public selectedFieldToAdd?: string;
  public selectedConfiguration: string[] = [];

  public newResourceConfigurationFieldEnum = newResourceConfigurationField;
  public configurationFieldStateEnum = configurationFieldState;

  private _newResourceTriggerModel: NewResourceFoundTriggerConfigModel = {};

  private _alreadyInitialized = false;

  constructor(
    private readonly _store: StoreService,
    private readonly _policiesHttpService: PoliciesHttpService,
    private readonly _httpErrorHandlerService: HttpErrorHandlerService,
    private readonly _beautifyPipe: BeautifyDisplayStringPipe,
    private readonly _resourceTypeDisplayNamePipe: ResourceTypeDisplayNamePipe,
    private readonly _labelsProviderPipe: LabelsProviderPipe,
    private readonly _playbookTemplatesService: PlaybookTemplatesService
  ) {}

  ngOnInit(): void {
    const integrationPlatform = this.playbooksTriggerInput.integration.platform;
    const unsupportedResourceTypes: ResourceType[] =
      unsupportedResourceTypesByPlatform[integrationPlatform] ?? unsupportedAllResourceTypes;

    this.resourceTypeOptions$ = this._playbookTemplatesService.getResourceTypeOptions$();

    const resourceTypeOptions: ISelectOption[] = createSelectOptionsFromStringArray(
      Object.values(ResourceType).filter(
        (value) =>
          !unsupportedResourceTypes.includes(value) &&
          ResourceTypesPerPlatform[this.playbooksTriggerInput.integration.platform].includes(value)
      ),
      this._resourceTypeDisplayNamePipe
    );
    this._playbookTemplatesService.setResourceTypeOptions(resourceTypeOptions);

    this._geConnectorsOptions();

    if (this.playbooksTriggerInput.triggerConfig) {
      this._newResourceTriggerModel = this.playbooksTriggerInput.triggerConfig as NewResourceFoundTriggerConfigModel;
    }

    this.fg.addControl('resourceTypes', new UntypedFormControl(this._newResourceTriggerModel?.resourceTypes || []));
    this.fg.addControl('connectors', new UntypedFormControl(this._newResourceTriggerModel?.connectors || []));
    this.fg.addControl('resourceStatus', new UntypedFormControl(this._newResourceTriggerModel?.resourceStatus || []));
    this.fg.addControl(
      'resourceCreatedBy',
      new UntypedFormControl(this._newResourceTriggerModel?.resourceCreatedBy || '', [AppValidators.email])
    );
    this.fg.addControl(
      'resourceLastModifiedBy',
      new UntypedFormControl(this._newResourceTriggerModel?.resourceLastModifiedBy || '')
    );
    this.fg.addControl(
      'resourceCreatedSince',
      new UntypedFormControl(this._newResourceTriggerModel?.resourceCreatedSince)
    );
    this.fg.addControl(
      'resourceCreatedUntil',
      new UntypedFormControl(this._newResourceTriggerModel?.resourceCreatedUntil)
    );
    this.fg.addControl(
      'resourceFirstSeenSince',
      new UntypedFormControl(this._newResourceTriggerModel?.resourceFirstSeenSince)
    );
    this.fg.addControl(
      'resourceFirstSeenUntil',
      new UntypedFormControl(this._newResourceTriggerModel?.resourceFirstSeenUntil)
    );
    this.fg.addControl(
      'resourceSecurityRisk',
      new UntypedFormControl(this._newResourceTriggerModel?.resourceSecurityRisk || [])
    );
    this.fg.addControl(
      'resourceBusinessCriticality',
      new UntypedFormControl(this._newResourceTriggerModel?.resourceBusinessCriticality || [])
    );
    this.fg.addControl(
      'resourceGenerativeAiOptions',
      new UntypedFormControl(this._newResourceTriggerModel?.resourceGenerativeAiOptions || [])
    );

    this.createdSinceDatesBehavioralSubject$ = new BehaviorSubject<Date | undefined>(
      this.fg.controls['resourceCreatedSince'].value
    );
    this.createdUntilDatesBehavioralSubject$ = new BehaviorSubject<Date | undefined>(
      this.fg.controls['resourceCreatedUntil'].value
    );
    this.firstSeenSinceDatesBehavioralSubject$ = new BehaviorSubject<Date | undefined>(
      this.fg.controls['resourceFirstSeenSince'].value
    );
    this.firstSeenUntilDatesBehavioralSubject$ = new BehaviorSubject<Date | undefined>(
      this.fg.controls['resourceFirstSeenUntil'].value
    );

    this._resetConfigurationFieldsState();
    this._setIrrelevantConfigurationFields(new Set(this.fg.controls['resourceTypes'].value));
    this._initSelectedConfigurationFields(this._newResourceTriggerModel);
    this._setConfigurationFieldsOptions();

    this.fg.controls['resourceCreatedBy'].valueChanges.subscribe(() =>
      this.validConfiguration.emit(this._checkFormIsValid())
    );
    this.fg.controls['resourceLastModifiedBy'].valueChanges.subscribe(() =>
      this.validConfiguration.emit(this._checkFormIsValid())
    );
  }

  public dateSelected(controlName: string, value?: Date): void {
    this.fg.controls[controlName].setValue(value);
    this.validConfiguration.emit(this._checkFormIsValid());
  }

  public updateSelectControl(controlName: string, option?: ISelectOption) {
    if (option) {
      this.fg.controls[controlName].setValue(option.id);
    } else {
      this.fg.controls[controlName].setValue(undefined);
    }
    this.validConfiguration.emit(this._checkFormIsValid());
  }

  public updateMultiSelectControl(controlName: string, options: ISelectOption[]) {
    this.fg.controls[controlName].setValue(options.map((option) => option.id));

    if (controlName == 'resourceTypes') {
      const selectedResourceTypes: Set<ResourceType> = new Set(new Set(this.fg.controls['resourceTypes'].value));
      if (selectedResourceTypes.size > 1) {
        this.multipleResourceTypesWarningVisible = true;
      } else {
        this.multipleResourceTypesWarningVisible = false;
      }
      this._setIrrelevantConfigurationFields(selectedResourceTypes);
      this._setConfigurationFieldsOptions();
    }
    this.validConfiguration.emit(this._checkFormIsValid());
  }

  public addSetting() {
    if (this.selectedFieldToAdd) {
      this.allConfigurationFieldsState[this.selectedFieldToAdd] = configurationFieldState.selected;
      this.selectedConfiguration = [this.selectedFieldToAdd].concat(this.selectedConfiguration);
      this._setConfigurationFieldsOptions();
      this.validConfiguration.emit(false);
    }
  }

  public removeSetting(fieldToRemove: string) {
    this._resetSettingState(fieldToRemove, configurationFieldState.unselected);
    this._setConfigurationFieldsOptions();
    this.validConfiguration.emit(this._checkFormIsValid());
  }

  public onConfigurationFieldSelection(selectedField?: ISelectOption) {
    this.selectedFieldToAdd = selectedField?.value;
    this.addSetting();
  }

  private _setConfigurationFieldsOptions() {
    this.selectedFieldToAdd = undefined;
    const unselectedFields = [];

    for (const field in this.allConfigurationFieldsState) {
      if (this.allConfigurationFieldsState[field] == configurationFieldState.unselected) {
        unselectedFields.push({
          id: field,
          value: field,
          displayName: this._labelsProviderPipe.transform(`playbooks.triggersConfigurationLabels.${field}`),
        } as ISelectOption);
      }
    }

    this.configurationFieldsOptions$.next(unselectedFields);
  }

  private _geConnectorsOptions() {
    this._policiesHttpService.getIntegrationPolicies(this.playbooksTriggerInput.integration.id).subscribe(
      (response: PolicyModel[]) => {
        const connectors = response[0].generalConnectorList;
        const connectorsOptions = connectors.map(
          (connector) =>
            ({
              id: connector.id,
              value: connector.id,
              displayName: connector.displayName,
            }) as ISelectOption
        );

        this.connectorsOptions$.next(connectorsOptions);
      },
      (error) => this._httpErrorHandlerService.handle(error)
    );
  }

  private _resetConfigurationFieldsState(): void {
    this.allConfigurationFieldsState = {};

    for (const field in newResourceConfigurationField) {
      this.allConfigurationFieldsState[field] = configurationFieldState.unselected;
    }

    // if this is not the first time the page loads
    // need to reset the control values when visibility changes
    // otherwise controls will have wrong values
    if (this._alreadyInitialized) {
      this.fg.controls['resourceCreatedBy'].setValue('');
      this.fg.controls['resourceCreatedSince'].setValue(null);
      this.fg.controls['resourceCreatedUntil'].setValue(null);
      this.fg.controls['resourceStatus'].setValue(null);
      this.fg.controls['resourceSecurityRisk'].setValue([]);
      this.fg.controls['resourceBusinessCriticality'].setValue([]);
      this.fg.controls['resourceGenerativeAiOptions'].setValue([]);
      this.fg.controls['resourceLastModifiedBy'].setValue('');
      this.fg.controls['resourceFirstSeenSince'].setValue(null);
      this.fg.controls['resourceFirstSeenUntil'].setValue(null);
      this.updateMultiSelectControl('connectors', []);

      // remove date string in ui (date-picker)
      this.createdSinceDatesBehavioralSubject$.next(undefined);
      this.createdUntilDatesBehavioralSubject$.next(undefined);
      this.firstSeenSinceDatesBehavioralSubject$.next(undefined);
      this.firstSeenUntilDatesBehavioralSubject$.next(undefined);
    } else {
      this._alreadyInitialized = true;
    }
  }

  private _resetConfigurationFieldsRelevance(): void {
    if (this.allConfigurationFieldsState) {
      for (const field in newResourceConfigurationField) {
        if (this.allConfigurationFieldsState[field] == configurationFieldState.irrelevant) {
          this.allConfigurationFieldsState[field] = configurationFieldState.unselected;
        }
      }
    }
  }

  private _setIrrelevantConfigurationFields(selectedResourceTypes: Set<ResourceType>) {
    this._resetConfigurationFieldsRelevance();

    const selectedResourceTypesArray = [...selectedResourceTypes]; // needed to be able to use 'every' function

    const platform = this.playbooksTriggerInput.integration.platform;
    const ppResourceTypesSupportingConnectorsFiler = new Set<ResourceType>([
      ResourceType.connection,
      ResourceType.flow,
    ]);
    const ppResourceTypesSupportingGenAiFiler = new Set<ResourceType>([ResourceType.canvas_app, ResourceType.flow]);
    const workatoResourceTypesSupportingConnectorsFiler = new Set<ResourceType>([
      ResourceType.connection,
      ResourceType.recipe,
    ]);

    switch (platform) {
      case Platform.salesforce:
        this._resetSettingsState(
          [newResourceConfigurationField.connectors, newResourceConfigurationField.generativeAiOptions],
          configurationFieldState.irrelevant
        );

        if (selectedResourceTypes.has(ResourceType.user)) {
          this._resetSettingsState(
            [newResourceConfigurationField.businessCriticality],
            configurationFieldState.irrelevant
          );
        }
        break;

      case Platform.chatGptEnterprise:
        this._resetSettingsState(
          [newResourceConfigurationField.connectors, newResourceConfigurationField.generativeAiOptions],
          configurationFieldState.irrelevant
        );
        break;

      case Platform.powerplatform:
        if (
          !(
            (selectedResourceTypes.size == 1 || selectedResourceTypes.size == 2) &&
            selectedResourceTypesArray.every((value) => ppResourceTypesSupportingConnectorsFiler.has(value))
          )
        ) {
          this._resetSettingsState([newResourceConfigurationField.connectors], configurationFieldState.irrelevant);
        }
        if (
          selectedResourceTypes.has(ResourceType.dlp_policy) ||
          selectedResourceTypes.has(ResourceType.dataverse_team) ||
          selectedResourceTypes.has(ResourceType.portal_page) ||
          selectedResourceTypes.has(ResourceType.connector) ||
          selectedResourceTypes.has(ResourceType.custom_connector)
        ) {
          this._resetSettingsState(
            [newResourceConfigurationField.businessCriticality],
            configurationFieldState.irrelevant
          );
        }
        if (
          selectedResourceTypes.has(ResourceType.azure_active_directory_group) ||
          selectedResourceTypes.has(ResourceType.user) ||
          selectedResourceTypes.has(ResourceType.environment)
        ) {
          this._resetSettingsState(
            [newResourceConfigurationField.businessCriticality, newResourceConfigurationField.lastModifiedBy],
            configurationFieldState.irrelevant
          );
        }
        if (
          selectedResourceTypes.has(ResourceType.gateway_cluster) ||
          selectedResourceTypes.has(ResourceType.gateway)
        ) {
          this._resetSettingsState(
            [
              newResourceConfigurationField.createdAt,
              newResourceConfigurationField.businessCriticality,
              newResourceConfigurationField.lastModifiedBy,
            ],
            configurationFieldState.irrelevant
          );
        }
        if (
          !selectedResourceTypesArray.every((resourceType) => ppResourceTypesSupportingGenAiFiler.has(resourceType))
        ) {
          this._resetSettingsState(
            [newResourceConfigurationField.generativeAiOptions],
            configurationFieldState.irrelevant
          );
        }

        break;
      case Platform.workato:
        if (
          !(
            (selectedResourceTypes.size == 1 || selectedResourceTypes.size == 2) &&
            selectedResourceTypesArray.every((value) => workatoResourceTypesSupportingConnectorsFiler.has(value))
          )
        ) {
          this._resetSettingsState([newResourceConfigurationField.connectors], configurationFieldState.irrelevant);
        }
        this._resetSettingsState(
          [
            newResourceConfigurationField.createdBy,
            newResourceConfigurationField.createdAt,
            newResourceConfigurationField.securityRisk,
            newResourceConfigurationField.businessCriticality,
            newResourceConfigurationField.lastModifiedBy,
            newResourceConfigurationField.generativeAiOptions,
          ],
          configurationFieldState.irrelevant
        );
        break;
      case Platform.powerBI:
        this._resetSettingsState(
          [
            newResourceConfigurationField.createdBy,
            newResourceConfigurationField.createdAt,
            newResourceConfigurationField.resourceStatus,
            newResourceConfigurationField.businessCriticality,
            newResourceConfigurationField.securityRisk,
            newResourceConfigurationField.lastModifiedBy,
            newResourceConfigurationField.firstSeenAt,
            newResourceConfigurationField.generativeAiOptions,
            newResourceConfigurationField.connectors,
          ],
          configurationFieldState.irrelevant
        );
        break;
      default:
        // platform that do not support inventory at all
        this._resetSettingsState(
          [newResourceConfigurationField.generativeAiOptions],
          configurationFieldState.irrelevant
        );
    }
  }

  private _initSelectedConfigurationFields(newResourceTriggerModel: NewResourceFoundTriggerConfigModel) {
    if (newResourceTriggerModel.resourceTypes && newResourceTriggerModel.resourceTypes.length > 0) {
      this.allConfigurationFieldsState[newResourceConfigurationField.resourceType] = configurationFieldState.selected;
    }
    if (newResourceTriggerModel.resourceStatus && newResourceTriggerModel.resourceStatus.length > 0) {
      this.allConfigurationFieldsState[newResourceConfigurationField.resourceStatus] = configurationFieldState.selected;
    }
    if (newResourceTriggerModel.resourceCreatedBy) {
      this.allConfigurationFieldsState[newResourceConfigurationField.createdBy] = configurationFieldState.selected;
    }
    if (newResourceTriggerModel.resourceLastModifiedBy) {
      this.allConfigurationFieldsState[newResourceConfigurationField.lastModifiedBy] = configurationFieldState.selected;
    }
    if (newResourceTriggerModel.resourceCreatedSince || newResourceTriggerModel.resourceCreatedUntil) {
      this.allConfigurationFieldsState[newResourceConfigurationField.createdAt] = configurationFieldState.selected;
    }
    if (newResourceTriggerModel.resourceFirstSeenSince || newResourceTriggerModel.resourceFirstSeenUntil) {
      this.allConfigurationFieldsState[newResourceConfigurationField.firstSeenAt] = configurationFieldState.selected;
    }
    if (newResourceTriggerModel.resourceSecurityRisk && newResourceTriggerModel.resourceSecurityRisk.length > 0) {
      this.allConfigurationFieldsState[newResourceConfigurationField.securityRisk] = configurationFieldState.selected;
    }
    if (
      newResourceTriggerModel.resourceBusinessCriticality &&
      newResourceTriggerModel.resourceBusinessCriticality.length > 0
    ) {
      this.allConfigurationFieldsState[newResourceConfigurationField.businessCriticality] =
        configurationFieldState.selected;
    }
    if (
      newResourceTriggerModel.resourceGenerativeAiOptions &&
      newResourceTriggerModel.resourceGenerativeAiOptions.length > 0
    ) {
      this.allConfigurationFieldsState[newResourceConfigurationField.generativeAiOptions] =
        configurationFieldState.selected;
    }
    if (newResourceTriggerModel.connectors && newResourceTriggerModel.connectors.length > 0) {
      this.allConfigurationFieldsState[newResourceConfigurationField.connectors] = configurationFieldState.selected;
    }
    for (const field in this.allConfigurationFieldsState) {
      if (this.allConfigurationFieldsState[field] == configurationFieldState.selected) {
        this.selectedConfiguration.push(field);
      }
    }
  }

  private _resetSettingsState(settings: string[], state: configurationFieldState) {
    for (const index in settings) {
      this._resetSettingState(settings[index], state);
    }
  }

  private _resetSettingState(setting: string, state: configurationFieldState) {
    this.allConfigurationFieldsState[setting] = state;
    const index = this.selectedConfiguration.indexOf(setting);
    if (index > -1) {
      this.selectedConfiguration.splice(index, 1);
    }
    this._resetFieldValue(setting);
  }

  private _resetFieldValue(setting: string) {
    if (setting == newResourceConfigurationField.businessCriticality) {
      this.fg.controls['resourceBusinessCriticality'].setValue([]);
    } else if (setting == newResourceConfigurationField.generativeAiOptions) {
      this.fg.controls['resourceGenerativeAiOptions'].setValue([]);
    } else if (setting == newResourceConfigurationField.createdAt) {
      this.fg.controls['resourceCreatedSince'].setValue(null);
      this.fg.controls['resourceCreatedUntil'].setValue(null);

      this.createdSinceDatesBehavioralSubject$.next(undefined);
      this.createdUntilDatesBehavioralSubject$.next(undefined);
    } else if (setting == newResourceConfigurationField.connectors) {
      this.updateMultiSelectControl('connectors', []);
    } else if (setting == newResourceConfigurationField.lastModifiedBy) {
      this.fg.controls['resourceLastModifiedBy'].setValue('');
    } else if (setting == newResourceConfigurationField.securityRisk) {
      this.fg.controls['resourceSecurityRisk'].setValue([]);
    } else if (setting == newResourceConfigurationField.createdBy) {
      this.fg.controls['resourceCreatedBy'].setValue('');
    } else if (setting == newResourceConfigurationField.firstSeenAt) {
      this.fg.controls['resourceFirstSeenSince'].setValue(null);
      this.fg.controls['resourceFirstSeenUntil'].setValue(null);

      this.firstSeenSinceDatesBehavioralSubject$.next(undefined);
      this.firstSeenUntilDatesBehavioralSubject$.next(undefined);
    } else if (setting == newResourceConfigurationField.resourceStatus) {
      this.updateMultiSelectControl('resourceStatus', []);
    } else if (setting == newResourceConfigurationField.resourceType) {
      this.fg.controls['resourceTypes'].setValue([]);
      this._setIrrelevantConfigurationFields(new Set<ResourceType>());
    }
  }

  private _checkFormIsValid(): boolean {
    if (this.selectedConfiguration.indexOf('resourceType') > -1 && this.fg.get('resourceTypes')?.value.length === 0) {
      return false;
    }
    if (this.selectedConfiguration.indexOf('connectors') > -1 && this.fg.get('connectors')?.value.length === 0) {
      return false;
    }
    if (
      this.selectedConfiguration.indexOf('resourceStatus') > -1 &&
      this.fg.get('resourceStatus')?.value.length === 0
    ) {
      return false;
    }
    if (
      this.selectedConfiguration.indexOf('resourceCreatedBy') > -1 &&
      this.fg.get('resourceCreatedBy')?.value === ''
    ) {
      return false;
    }
    if (
      this.selectedConfiguration.indexOf('resourceLastModifiedBy') > -1 &&
      this.fg.get('resourceLastModifiedBy')?.value === ''
    ) {
      return false;
    }
    if (
      this.selectedConfiguration.indexOf('resourceSecurityRisk') > -1 &&
      this.fg.get('resourceSecurityRisk')?.value.length === 0
    ) {
      return false;
    }
    if (
      this.selectedConfiguration.indexOf('resourceBusinessCriticality') > -1 &&
      this.fg.get('resourceBusinessCriticality')?.value.length === 0
    ) {
      return false;
    }
    if (
      this.selectedConfiguration.indexOf('resourceGenerativeAiOptions') > -1 &&
      this.fg.get('resourceGenerativeAiOptions')?.value.length === 0
    ) {
      return false;
    }
    if (
      this.selectedConfiguration.indexOf('resourceCreatedAt') > -1 &&
      !this.fg.get('resourceCreatedSince')?.value &&
      !this.fg.get('resourceCreatedUntil')?.value
    ) {
      return false;
    }
    if (
      this.selectedConfiguration.indexOf('resourceFirstSeenAt') > -1 &&
      !this.fg.get('resourceFirstSeenSince')?.value &&
      !this.fg.get('resourceFirstSeenUntil')?.value
    ) {
      return false;
    }

    return true;
  }

  private _createGenerativeAiOptionsSelectOptions(): ISelectOption[] {
    return GenerativeAiOptionsSelectOptions.map(
      (option) =>
        ({
          id: option.value, // In this component the id of the selected option is using as the value
          value: option.value,
          displayName: option.displayName,
        }) as ISelectOption
    );
  }
}
